import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatMoney, parseAmountToSen, parseCaptureParams } from '@wis/shared';
import { useApp } from '../state/AppContext.js';
import { addTransaction } from '../db/repository.js';
import { Notice } from '../components/primitives.js';
import { QuickEntryForm, type QuickEntryValues } from '../components/QuickEntryForm.js';

/**
 * `/capture` -- the URL a Shortcut can open when the app is in the foreground.
 *
 * Everything in that URL is untrusted input from outside the app, so:
 *   - every field is validated on its own with the shared Zod schema, so one
 *     bad parameter cannot strand the person on an error page holding a
 *     receipt -- the good fields prefill, the bad ones are named;
 *   - nothing is written until the person confirms what they are looking at;
 *   - the parameters are stripped out of the address bar with `replaceState`
 *     the moment they are read, so an amount and a merchant do not sit in the
 *     browser history or get carried into a shared link.
 */
export function Capture(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { categories, accounts, rules, merchantHistory, settings, toast } = useApp();
  const [scrubbed, setScrubbed] = useState(false);

  // Read once, on the first render, before anything can strip them.
  const raw = useMemo(() => Object.fromEntries(searchParams.entries()), [searchParams]);

  const { params, problems } = useMemo(() => parseCaptureParams(raw), [raw]);

  useEffect(() => {
    if (scrubbed) return;
    // Replace, not push: the parameters never enter the history stack.
    window.history.replaceState(window.history.state, '', '/capture');
    setScrubbed(true);
  }, [scrubbed]);

  // Whole sen is unambiguous, so it wins when both are present. The Ringgit
  // form goes through the same string parser the rest of the app uses.
  const amountSen = params.amount
    ? Number(params.amount)
    : params.amountRm
      ? Math.abs(parseAmountToSen(params.amountRm))
      : 0;

  async function handleSubmit(values: QuickEntryValues): Promise<void> {
    await addTransaction({
      ...values,
      source: params.source,
      currency: settings.currency,
      // It arrived from outside the app, so the person confirmed it rather than
      // typed it -- but they *have* now seen it, so it is not a guess.
      importConfidence: 1,
      reviewStatus: 'confirmed',
    });
    toast(`Saved ${formatMoney(values.amountSen, { currency: settings.currency })}`);
    navigate('/', { replace: true });
  }

  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <Notice tone={problems.length > 0 ? 'warning' : 'info'}>
        {problems.length > 0
          ? `${problems.join(' ')} Fill in what is missing below -- nothing has been recorded yet.`
          : 'This came from a Shortcut. Check it before saving -- nothing has been recorded yet.'}
      </Notice>

      <div style={{ marginTop: 'var(--space-4)' }}>
        <QuickEntryForm
          categories={categories}
          accounts={accounts}
          rules={rules}
          history={merchantHistory}
          timeZone={settings.timeZone}
          defaultAccountLabel={params.account ?? settings.defaultAccountLabel}
          defaultCategoryId={params.category ?? settings.defaultCategoryId}
          currency={settings.currency}
          initial={{
            ...(amountSen > 0 ? { amountSen } : {}),
            ...(params.merchant ? { merchant: params.merchant } : {}),
            ...(params.category ? { categoryId: params.category } : {}),
            ...(params.account ? { accountLabel: params.account } : {}),
            ...(params.type ? { type: params.type } : {}),
            ...(params.paidAt ? { occurredAt: params.paidAt } : {}),
            ...(params.note ? { note: params.note } : {}),
          }}
          submitLabel="Confirm and save"
          autoFocusAmount={amountSen === 0}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/', { replace: true })}
        />
      </div>
    </div>
  );
}
